import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import AutomatedMessageModal from '../components/AutomatedMessageModal';
import CancelButton from '../components/CancelButton';
import Loading from '../components/Loading';
import {
  AutomatedMessage,
  automatedMessageApi,
  AutomatedMessageLog,
  AutomatedMessageStats,
  CreateAutomatedMessageRequest,
  getRuleDescription,
  getTriggerTypeIcon,
  triggerTypeLabels,
} from '../services/automatedMessageApi';
import { colors } from '../theme';

const DietitianAutomatedMessagesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [rules, setRules] = useState<AutomatedMessage[]>([]);
  const [logs, setLogs] = useState<AutomatedMessageLog[]>([]);
  const [stats, setStats] = useState<AutomatedMessageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomatedMessage | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesData, logsData, statsData] = await Promise.all([
        automatedMessageApi.getAll(),
        automatedMessageApi.getLogs(100),
        automatedMessageApi.getStats(),
      ]);
      setRules(rulesData);
      setLogs(logsData);
      setStats(statsData);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      setSnackbar({
        open: true,
        message: 'Veriler yüklenirken hata oluştu',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateRule = async (data: CreateAutomatedMessageRequest) => {
    try {
      if (editingRule) {
        await automatedMessageApi.update(editingRule.id, data);
        setSnackbar({
          open: true,
          message: 'Kural başarıyla güncellendi',
          severity: 'success',
        });
      } else {
        await automatedMessageApi.create(data);
        setSnackbar({
          open: true,
          message: 'Kural başarıyla oluşturuldu',
          severity: 'success',
        });
      }
      loadData();
      setEditingRule(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Kural kaydedilirken hata oluştu',
        severity: 'error',
      });
      throw error;
    }
  };

  const handleToggleRule = async (id: string) => {
    try {
      await automatedMessageApi.toggle(id);
      loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Kural durumu değiştirilemedi',
        severity: 'error',
      });
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;

    try {
      await automatedMessageApi.delete(ruleToDelete);
      setSnackbar({
        open: true,
        message: 'Kural başarıyla silindi',
        severity: 'success',
      });
      loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Kural silinirken hata oluştu',
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleSendNow = async (ruleId: string) => {
    try {
      const result = await automatedMessageApi.sendNow(ruleId);
      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success',
      });
      loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Mesaj gönderilemedi',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Otomatik mesajlar yükleniyor..."
      />
    );
  }

  return (
    <Box >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flex={1}
            minWidth={0}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: colors.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SmartToyIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box minWidth={0} flex={1}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: colors.text.primary }}
                noWrap
              >
                Otomatik Mesajlar
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                Danışanlarınıza otomatik mesaj kuralları oluşturun ve yönetin
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingRule(null);
                setModalOpen(true);
              }}
              sx={{
                bgcolor: colors.primary.main,
                color: 'white',
                '&:hover': { bgcolor: colors.hover.primary },
                textTransform: 'none',
                px: 3,
                py: 1,
                borderRadius: 2,
              }}
            >
              Yeni Kural
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: colors.primary.light,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TimelineIcon sx={{ color: colors.primary.main, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: colors.text.primary }}>
                      {stats.totalRules}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      Toplam Kural
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: colors.alpha.primary10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayArrowIcon sx={{ color: colors.status.success, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: colors.text.primary }}>
                      {stats.activeRules}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      Aktif Kural
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(66, 165, 245, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SendIcon sx={{ color: colors.status.info, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: colors.text.primary }}>
                      {stats.messagesThisWeek}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      Bu Hafta
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(255, 167, 38, 0.28)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HistoryIcon sx={{ color: colors.status.warning.main, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: colors.text.primary }}>
                      {stats.totalMessagesSent}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      Toplam Gönderim
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            borderBottom: `2px solid ${colors.border.light}`,
            bgcolor: colors.background.light,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 56,
              fontSize: '0.95rem',
              color: colors.text.secondary,
              '& svg': {
                color: 'inherit',
              },
              '&:hover': {
                bgcolor: colors.primary.main,
                color: 'white !important',
                '& svg': {
                  color: 'white !important',
                },
              },
              '&.Mui-selected': {
                color: `${colors.primary.main} !important`,
                '& svg': {
                  color: `${colors.primary.main} !important`,
                },
                '&:hover': {
                  bgcolor: colors.primary.main,
                  color: 'white !important',
                  '& svg': {
                    color: 'white !important',
                  },
                },
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: colors.primary.main,
              height: 3,
            },
          }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <TimelineIcon fontSize="small" />
                Kurallar ({rules.length})
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <HistoryIcon fontSize="small" />
                Gönderim Geçmişi ({logs.length})
              </Box>
            }
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Rules Tab */}
          {activeTab === 0 && (
            <>
              {rules.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    gutterBottom
                  >
                    Henüz otomatik mesaj kuralı oluşturmadınız
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    İlk kuralınızı oluşturarak danışanlarınıza otomatik mesaj
                    göndermeye başlayın
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingRule(null);
                      setModalOpen(true);
                    }}
                    sx={{
                      bgcolor: colors.primary.main,
                      '&:hover': { bgcolor: colors.hover.primary },
                    }}
                  >
                    İlk Kuralı Oluştur
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  {rules.map((rule) => (
                    <Card
                      key={rule.id}
                      elevation={2}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${
                          rule.isActive
                            ? colors.primary.light
                            : colors.border.light
                        }`,
                        opacity: rule.isActive ? 1 : 0.7,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        bgcolor: colors.background.paper,
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)',
                          borderColor: colors.primary.light,
                        },
                      }}
                    >
                        <CardContent>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={2}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="h5" sx={{ color: colors.text.primary }}>
                                {getTriggerTypeIcon(rule.triggerType)}
                              </Typography>
                              <Box>
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight={600}
                                  sx={{ color: colors.text.primary }}
                                >
                                  {rule.title}
                                </Typography>
                                <Chip
                                  label={
                                    triggerTypeLabels[rule.triggerType] ||
                                    rule.triggerType
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor: colors.alpha.primary10,
                                    color: colors.primary.dark,
                                    fontWeight: 500,
                                  }}
                                />
                              </Box>
                            </Box>
                            <Chip
                              label={rule.isActive ? 'Aktif' : 'Pasif'}
                              size="small"
                              sx={{
                                bgcolor: rule.isActive
                                  ? colors.status.success
                                  : colors.text.disabled,
                                color: 'white',
                              }}
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2,
                              color: colors.text.secondary,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {rule.content}
                          </Typography>

                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: colors.background.light,
                              borderRadius: 1,
                              mb: 2,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              📋 {getRuleDescription(rule)}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              👥{' '}
                              {rule.clientName
                                ? rule.clientName
                                : 'Tüm danışanlar'}
                            </Typography>
                            {rule.lastSentAt && (
                              <>
                                <br />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  📨 Son gönderim:{' '}
                                  {new Date(rule.lastSentAt).toLocaleDateString(
                                    'tr-TR'
                                  )}
                                </Typography>
                              </>
                            )}
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              📊 Toplam {rule.totalSentCount} mesaj gönderildi
                            </Typography>
                          </Box>

                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            gap={1}
                          >
                            <Tooltip
                              title={rule.isActive ? 'Duraklat' : 'Aktifleştir'}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleToggleRule(rule.id)}
                                sx={{
                                    width:'fit-content',
                                  color: rule.isActive
                                    ? colors.status.warning.main
                                    : colors.status.success,
                                }}
                              >
                                {rule.isActive ? (
                                  <PauseIcon />
                                ) : (
                                  <PlayArrowIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Şimdi Gönder">
                              <IconButton
                                size="small"
                                onClick={() => handleSendNow(rule.id)}
                                sx={{ color: colors.status.success, width: 'fit-content' }}
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Düzenle">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingRule(rule);
                                  setModalOpen(true);
                                }}
                                sx={{ color: colors.primary.main,width:'fit-content' }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setRuleToDelete(rule.id);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{ color: colors.status.error,width:'fit-content' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                  ))}
                </Box>
              )}
            </>
          )}

          {/* Logs Tab */}
          {activeTab === 1 && (
            <>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadData}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: colors.text.primary,
                    borderColor: colors.border.light,
                    '&:hover': {
                      borderColor: colors.primary.main,
                      bgcolor: 'transparent',
                      color: colors.primary.main,
                    },
                  }}
                >
                  Yenile
                </Button>
              </Box>

              {logs.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary">
                    Henüz mesaj gönderimi yapılmadı
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Kurallar aktifleştirildiğinde gönderilen mesajlar burada
                    görünecek
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {logs.map((log) => (
                    <Card
                      key={log.id}
                      elevation={2}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${colors.border.light}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                          }}
                        >
                      <Box>
                        <Typography 
                          variant="subtitle2" 
                          fontWeight={600}
                          sx={{ color: colors.text.primary }}
                        >
                          {getTriggerTypeIcon(log.triggerType)} {log.clientName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.text.secondary,
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            maxWidth: 400,
                          }}
                        >
                          {log.messageContent}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={
                            triggerTypeLabels[log.triggerType] || log.triggerType
                          }
                          size="small"
                          sx={{
                            bgcolor: colors.alpha.primary10,
                            color: colors.primary.dark,
                          }}
                        />
                        <Chip
                          label={log.isDelivered ? 'Gönderildi' : 'Bekliyor'}
                          size="small"
                          sx={{
                            bgcolor: log.isDelivered
                              ? colors.status.success
                              : colors.status.warning.main,
                            color: 'white',
                          }}
                        />
                        <Typography 
                          variant="caption" 
                          sx={{ color: colors.text.secondary }}
                        >
                          {new Date(log.sentAt).toLocaleString('tr-TR')}
                        </Typography>
                      </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Create/Edit Modal */}
      <AutomatedMessageModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingRule(null);
        }}
        onSave={handleCreateRule}
        editingRule={editingRule}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Kuralı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu otomatik mesaj kuralını silmek istediğinizden emin misiniz? Bu
            işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={() => setDeleteDialogOpen(false)} />
          <Button onClick={handleDeleteRule} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DietitianAutomatedMessagesPage;
